import { getDynamicEnv } from '@/utils/configManager';
import * as Sentry from '@sentry/react-native';
import { fetchAuthSession } from 'aws-amplify/auth';

async function getBaseApiUrl(branch_short_name: string | null | undefined, apifolder: string = 'query'): Promise<string> {
  const folder = apifolder || 'query';
  // Get current dynamic configuration at runtime
  const dynamicEnv = await getDynamicEnv();
  const apiHost = dynamicEnv.apiHost;
  
  if (branch_short_name && branch_short_name !== '') {
    return `https://${branch_short_name}.${apiHost}/${folder}`;
  }
  return `https://tewkesbury.${apiHost}/${folder}`;
}

/**
 * Minimal authenticated fetch wrapper using Amplify Auth Gen 2.
 * Attaches idToken and accessToken to request headers if available.
 * Uses dynamic configuration based on current user (reviewer vs non-reviewer).
 */
export async function authenticatedFetch(
  branch_short_name: string | null | undefined,
  path: string,
  init: RequestInit = {},
  apifolder: string = 'query',
  suppressErrors: boolean = false
): Promise<Response> {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  const accessToken = session.tokens?.accessToken?.toString();
  const headers = new Headers(init.headers || {});
  if (idToken) headers.set('votes-now-id-token', idToken);
  if (accessToken) headers.set('votes-now-access-token', accessToken);
  headers.set('Content-Type', 'application/json');
  const baseUrl = await getBaseApiUrl(branch_short_name, apifolder);
  const url = path.startsWith('http') ? path : `${baseUrl}${path}`;
  const response = await fetch(url, { ...init, headers });
  if (!response.ok) {
    let errorMessage = `Server error: ${response.status}`;
    let traceback = null;
    try {
      // Clone the response before reading it to avoid "Already read" errors
      const responseClone = response.clone();
      const contentType = responseClone.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await responseClone.json();
        if (data && data.error) errorMessage = data.error;
        else if (typeof data === 'string') errorMessage = data;
        else errorMessage = JSON.stringify(data);
        if (data?.traceback) traceback = data.traceback;
      } else {
        const text = await responseClone.text();
        if (text) errorMessage = text;
      }
    } catch (e) {
      // If we can't read the response body, just use the status
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }

    const isNetworkError = errorMessage.includes('Load failed') || 
        errorMessage.includes('Network request failed') ||
        errorMessage.includes('fetch') ||
        errorMessage.includes('timeout') ||
        errorMessage.includes('connection');

    if (isNetworkError) {
      console.warn('Network error (handled by retry logic):', errorMessage);
    } else if (!suppressErrors) {
      const error = new Error(errorMessage);
      // Attach the response for further inspection if needed
      (error as any).response = response;
      Sentry.captureException(error, {
        extra: {
          url,
          method: init.method || 'GET',
          requestBody: init.body,
          responseStatus: response.status,
          responseStatusText: response.statusText,
          responseUrl: response.url,
          traceback: traceback,
          branch_short_name,
          apifolder,
          path,
        },
      });
      alert(error);
    }
  }
  return response;
}




// Add a default export to satisfy the route requirement
export default {
  authenticatedFetch,
}; 